import axios from 'axios';
import { parseString } from 'xml2js';
import _ from 'lodash';

import {
	SELECT_ANSWER,
	SELECT_TEST_ANSWER,
	SHOW_FEEDBACK,
	HIDE_FEEDBACK,
	SET_ITEMS,
	SET_ITEMS_AAXML,
	SET_ITEMS_KEYS,
	GO_TO_PREVIOUS_ITEM,
	GO_TO_NEXT_ITEM,
	GO_TO_ITEM,
	RECOVER_SAVED_STATE,
	SHOW_RESULTS,
	RESTART_EXERCISE,
	SET_INTERACTION,
	SET_CORRECT_ANSWERS,
	SET_VERSION,
	SET_TEST_MODE,
	SET_TEST_SENT,
	START_RECORDING,
	STOP_RECORDING,
	SET_EX_CONTAINER,
	SET_AUTO_NUM,
	SET_RANDOM_ITEMS_ORDER,
	SET_RANDOM_ITEMS,
	MARK_ITEM_WITH_AUDIO,
	SET_EXERCISE_READY,
	SET_ITEMS_TO_SHOW
} from './types';

const API_URL = window.NL_CONFIG.api.url;

let apiRequestConfig = {
	withCredentials: true
};

// development
if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
	const auth = require('../config/auth.js').default; // eslint-disable-line global-require
	apiRequestConfig.auth = auth;
} else { // production
	apiRequestConfig = {
		headers: {
			'X-Session-Cookie-Name': window.discovia && window.discovia.directory
		},
	};
}

export const recoverSavedState = (state) => ({ type: RECOVER_SAVED_STATE, payload: state });
export const showResults = () => ({ type: SHOW_RESULTS });
export const restartExercise = () => ({ type: RESTART_EXERCISE });
export const setInteraction = (interaction) => ({ type: SET_INTERACTION, payload: interaction });
export const setVersion = (version) => ({ type: SET_VERSION, payload: version });
export const setAutoNum = (autoNum) => ({ type: SET_AUTO_NUM, payload: autoNum });
export const setTestMode = (testMode) => ({ type: SET_TEST_MODE, payload: testMode });
export const setExContainer = (exContainer) => ({ type: SET_EX_CONTAINER, payload: exContainer });
export const goToPreviousItem = () => ({ type: GO_TO_PREVIOUS_ITEM });
export const goToNextItem = () => ({ type: GO_TO_NEXT_ITEM });
export const goToItem = (index) => ({ type: GO_TO_ITEM, payload: index });
export const setItemsToShow = (itemsToShow) => ({ type: SET_ITEMS_TO_SHOW, payload: itemsToShow });
export const setRandomItems = (randomItems) => ({ type: SET_RANDOM_ITEMS, payload: randomItems });

export const setItems = (items) => async (dispatch, getState) => {
	items.forEach((e, i) => e.itemIndex = i); // necesario para el save state (usado en updateInteraction)
	dispatch({ type: SET_ITEMS, payload: items });
};

export const showFeedback = (type, loading, messageId, fromItem) => async (dispatch, getState) => {
	dispatch({
		type: SHOW_FEEDBACK,
		payload: {
			index: fromItem === undefined ? getState().exercise.currentItem : fromItem,
			type,
			loading,
			messageId
		}
	});
};

export const setCorrectAnswers = () => async (dispatch, getState) => {
	const { exercise: { items, answersSelected }, loader } = getState();
	const { interaction } = loader;
	const correctAnswers = [];

	items.forEach((item, i) => {
		const answerSelected = item.answer[answersSelected[i]];
		correctAnswers[i] = answerSelected && answerSelected.$ && answerSelected.$.correct === 'true';
	});

	const totalCorrectAnswers = correctAnswers.filter(c => c).length;

	if (interaction) {
		interaction.setScore(correctAnswers, items.length);
	}

	dispatch({
		type: SET_CORRECT_ANSWERS,
		payload: {
			correctAnswers,
			total: totalCorrectAnswers
		}
	});
};

export const selectAnswer = (answer, fromItem) => async (dispatch, getState) => {
	const { exercise, loader } = getState();
	const { items } = exercise;
	const { testMode, testSent } = loader;
	const item = fromItem === undefined ? exercise.currentItem : fromItem;

	if (answer === undefined) return;

	const currentAnswer = items[item].answer[answer];

	if (testMode && !testSent) {
		dispatch({ type: SELECT_TEST_ANSWER, payload: answer });
	} else {
		dispatch({ type: SELECT_ANSWER, payload: { answerSelected: answer, fromItem: item } });
	}

	if (!testSent) {
		dispatch(setCorrectAnswers());
	}

	if (testMode && !testSent) {
		dispatch(showFeedback('recorded', null, null, item));
	} else if (currentAnswer.$ && currentAnswer.$.correct === 'true') {
		dispatch(showFeedback('correct', null, null, item));
	} else {
		dispatch(showFeedback('incorrect', null, null, item));
	}
};

export const setTestSent = () => async (dispatch, getState) => {
	const { exercise: { items, answersSelected } } = getState();
	dispatch({ type: SET_TEST_SENT });
	items.forEach((item, i) => dispatch(selectAnswer(answersSelected[i], i)));
};

export const hideFeedback = () => async (dispatch, getState) => {
	const { exercise: { currentItem } } = getState();
	dispatch({ type: HIDE_FEEDBACK, payload: { index: currentItem } });
};

export const analyzeAudio = (blob) => async (dispatch, getState) => {
	// return dispatch(selectAnswer(Math.floor(Math.random() * 3) + 1));
	try {
		dispatch(showFeedback('analysis', true));

		const { interaction } = getState().loader;
		const { exercise: { itemsAAXML, currentItem }, loader: { itemsKeys } } = getState();
		const data = { aaxml: itemsAAXML[currentItem], key: itemsKeys[currentItem] };
		const apiUrl = `${API_URL}/audioanalysis/${(window.NL_CONFIG && window.NL_CONFIG.isSample) ? 'sample-url' : 'url'}`;
		const { data: { remoteURL, s3SignedURL } } = await axios.post(apiUrl, data, apiRequestConfig);

		const aaData = new FormData();
		aaData.append('audio', blob);
		aaData.audio = blob;

		const aaRes = await axios({
			method: 'post',
			url: remoteURL,
			data: aaData,
			config: { headers: { 'Content-Type': 'multipart/form-data' } }
		});

		if (!(window.userSession && window.userSession.user && window.userSession.user.readonly) && interaction && !window.NL_CONFIG.isSample && !window.concurrentUser) {
			await axios({
				method: 'put',
				url: s3SignedURL,
				data: blob,
				config: { headers: { 'Content-Type': blob.type } }
			});

			dispatch({ type: MARK_ITEM_WITH_AUDIO, payload: currentItem });
		}

		parseString(aaRes.data, (err, result) => {
			dispatch(hideFeedback());

			if (err) window.alert(err.message);
			if (result.message.AAResult[0].error) {
				const errors = result.message.AAResult[0].error.join('\n - ');
				alert(`DESIGN-TIME ERROR\n\nThe following errors were returned by the analysis API:\n - ${errors}`);
			}

			if (window.NL_CONFIG.sound.enabled) {
				window.soundManager.play('result');
			}

			let selectedAnswerId;
			try {
				selectedAnswerId = result.message.AAResult[0].analysis[0].MCQSummary[0].selectedOption[0].$.id;
			} catch (e) {
				dispatch(showFeedback('quality', null, 256));
				return;
			}

			if (selectedAnswerId) {
				dispatch(selectAnswer(Number(selectedAnswerId)));
			} else {
				const audioQualityMsgId = window.loader.exercises.common.audioanalysis.getAudioQualityMsgId(result.message.AAResult[0].audioQuality[0].$.status);
				if (audioQualityMsgId) {
					dispatch(showFeedback('quality', null, audioQualityMsgId));
				} else {
					dispatch(showFeedback('notRecognized'));
				}
			}
		});
	} catch (e) {
		alert(e.message);
	}
};

const getAAXMLAnswers = (item) =>
	item.answer.map((answer, i) => {
		const correct = answer.$ && answer.$.correct === 'true';
		const questionText = item.question[0]._;
		const replace = questionText.match(/_{2,}/g);
		const finalAnswer = replace ? questionText.replace(/_{2,}/g, answer._) : answer._;
		return `<answer id="${i}" semantic="${correct ? 'correct' : 'incorrect'}">${finalAnswer}</answer>`;
	}).join('');

export const processItems = () => async (dispatch, getState) => {
	const { interaction } = getState().loader;
	const { items, itemsToShow, randomItems, randomItemsOrder } = getState().exercise;

	let processedItems = items;

	if (randomItems) {
		let currentRandomItemsOrder = [];
		const randomizedItems = [];

		if (randomItemsOrder) {
			currentRandomItemsOrder = randomItemsOrder;
		} else {
			currentRandomItemsOrder = window.Utils.shuffle(processedItems.map(item => item.itemIndex));
			dispatch({ type: SET_RANDOM_ITEMS_ORDER, payload: currentRandomItemsOrder });
		}

		for (let i = 0; i < processedItems.length; i++) {
			randomizedItems.push(_.find(processedItems, (item) => item.itemIndex === currentRandomItemsOrder[i]));
		}

		processedItems = randomizedItems;
	}

	if (itemsToShow) {
		processedItems = processedItems.slice(0, itemsToShow);
	}

	dispatch({ type: SET_ITEMS, payload: processedItems });

	const itemsAAXML = processedItems.map(item => `<AARequest><analysis analysisType="MCQ">${getAAXMLAnswers(item)}</analysis ></AARequest >`);

	dispatch({
		type: SET_ITEMS_AAXML,
		payload: itemsAAXML
	});

	if (interaction && !window.concurrentUser) {
		const itemsKeys = processedItems.map((item, i) => {
			const { unitId } = window.NL_CONFIG;
			const resourceId = window.API._currentScoId; // eslint-disable-line
			const interactionIndex = interaction.index;
			if (unitId !== undefined && resourceId !== undefined && interactionIndex !== undefined) {
				return `${unitId}/${resourceId}/${interactionIndex}/${i}`;
			}
			return undefined;
		});

		dispatch({
			type: SET_ITEMS_KEYS,
			payload: itemsKeys
		});
	}

	dispatch({ type: SET_EXERCISE_READY });
};

export const setInteractionInterface = (interactionInterface) => async (dispatch, getState) => {
	const interaction = window.nl_loader.getNextInteraction();

	if (interaction) {
		interaction.setExerciseInteractionInterface(interactionInterface);
		dispatch(setInteraction(interaction));
	}
};

export const startRecording = () => async (dispatch, getState) => {
	dispatch({ type: START_RECORDING });
	dispatch(showFeedback('recording'));
};

export const stopRecording = (data) => async (dispatch, getState) => {
	dispatch({ type: STOP_RECORDING });
	dispatch(hideFeedback());
	dispatch(analyzeAudio(data));
};
